import React, { FC, ReactNode } from "react";
import classNames from "classnames";

/**
 * The one panel shell, per the design handoff §04. Instantiated four times
 * (Stories · Thumbnails · Transcripts · Performance) so there is a single
 * section header, a single count treatment and a single empty/loading/error
 * vocabulary instead of the two competing header patterns the page had.
 *
 * PAGE-LOCAL ON PURPOSE. It does not touch `EmptyPanel` (24 call sites across
 * the app) or `EmbedCard` (the Stories page), both of which would otherwise be
 * restyled out from under other surfaces.
 */

/** Every panel drives its body from one of these. */
export type PanelStatus = "loading" | "ready" | "error";

export const CountPill: FC<{ value: number }> = ({ value }) => (
  <span
    className={classNames(
      "inline-flex items-center h-5 px-2 rounded-full bg-surface-2 tabular-nums shrink-0",
      value === 0 ? "text-muted" : "text-text-2",
    )}
    style={{ fontSize: 11 }}
  >
    {value}
  </span>
);

interface PanelActionProps {
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
}

/** The 28px pill that sits at the right edge of a section header. */
export const PanelAction: FC<PanelActionProps> = ({
  label,
  icon,
  onClick,
  disabled = false,
  title,
}) => (
  <button
    type="button"
    title={title || label}
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    className={classNames(
      "inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-surface-2 shrink-0 transition-colors",
      disabled
        ? "text-muted cursor-not-allowed"
        : "text-text-1 cursor-pointer hover:bg-track",
    )}
    style={{ fontSize: 11.5 }}
  >
    {icon}
    {label}
  </button>
);

interface VideoPanelProps {
  title: string;
  /** Omit entirely for panels that have no meaningful count. */
  count?: number;
  action?: ReactNode;
  /** A state chip beside the title — "Unavailable", "Delayed" — for panels whose
   *  header has to say something the body alone cannot. */
  badge?: ReactNode;
  children: ReactNode;
  /** Anchor target for the jump rail. */
  id?: string;
  /** Panels in the asset row stretch to the tallest sibling. */
  fill?: boolean;
  className?: string;
}

export const VideoPanel: FC<VideoPanelProps> = ({
  title,
  count,
  action,
  badge,
  children,
  id,
  fill = false,
  className,
}) => (
  <section
    id={id}
    className={classNames(
      "bg-surface border border-border-token rounded-xl p-4 sm:p-5 min-w-0",
      { "flex flex-col": fill },
      className,
    )}
    style={{ boxShadow: "var(--sh-1)" }}
  >
    <div className="flex items-center gap-[9px]">
      <h2
        className="m-0 font-CircularStdBlack text-text-1"
        style={{ fontSize: 16, lineHeight: 1.2 }}
      >
        {title}
      </h2>
      {count != null && <CountPill value={count} />}
      {badge}
      {action && (
        <>
          <span className="flex-1" />
          {action}
        </>
      )}
    </div>
    <div className={classNames({ "flex-1 flex flex-col": fill })} style={{ marginTop: 14 }}>
      {children}
    </div>
  </section>
);

export default VideoPanel;
